import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import { cloneDeep } from 'lodash';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import CollapsibleCategoryButtons from 'lib/CollapsibleCategory/CollapsibleCategoryButtons';
import SelectOption from 'lib/SelectOption';
import { CodeNameModel, FileContent, FileList } from 'components/common/types';
import frameSvg from 'assets/icons/frame.svg';
import { getIsReseller } from 'store/publisher/selectors';
import { updateFrameSelection } from 'utils/frameList';
import { MediaOwnerLabel } from '../ListOfFiles/shared/MediaOwnerLabel';
import ListOfFilesWithIncludeExcludeProps from './ListOfFilesWithIncludeExclude.types';
import ListOfFiles from '../ListOfFiles/ListOfFiles';

const getFileStats = ({ fileContents = [] }: FileList): { available: number; included: number; excluded: number } => ({
  available: fileContents.length,
  included: fileContents.filter(({ include }) => !!include).length,
  excluded: fileContents.filter(({ include }) => include === false).length,
});

const ListOfFilesWithIncludeExclude = ({
  uploadedListOfFiles,
  setUploadedListOfFiles,
  openCategories,
  onToggleCategory,
  onRemoveFile,
  isIncludeExcludeDisabled = false,
}: ListOfFilesWithIncludeExcludeProps): React.ReactElement => {
  const isReseller = useSelector(getIsReseller);

  const isOptionSelected = (code: string, index: number): boolean =>
    !!uploadedListOfFiles?.[index]?.fileContents?.find((option) => option.code === code);

  const onOptionIncludeExcludeChange = (value: CodeNameModel, index: number): void => {
    const uploadedFiles = cloneDeep(uploadedListOfFiles);
    const contents = uploadedFiles[index].fileContents;

    if (!contents) return;

    const updatedContents = updateFrameSelection(contents, value, false);

    uploadedFiles[index].fileContents = updatedContents;
    uploadedFiles[index].include = undefined;

    setUploadedListOfFiles(uploadedFiles);
  };

  const onEntireFileIncludeExclude = ({ name, include }: CodeNameModel): void => {
    if (include === undefined) return;

    const uploadedFiles = cloneDeep(uploadedListOfFiles);
    const currentFile = uploadedFiles.find((file) => file.name === name);

    if (!currentFile?.fileContents) return;

    currentFile.fileContents = currentFile.fileContents.map((content) => ({ ...content, include }));
    currentFile.include = include;

    setUploadedListOfFiles(uploadedFiles);
  };

  const handleIncludeExcludeChange = !isIncludeExcludeDisabled
    ? (value: CodeNameModel, fileIndex: number) => onOptionIncludeExcludeChange(value, fileIndex)
    : undefined;

  const renderItem = (
    { code, name, include, mediaOwner }: FileContent,
    fileIndex: number,
    style: React.CSSProperties,
  ): React.ReactElement => {
    return (
      <div
        className="border-b border-neutral-950-opacity-10 last:border-b-0"
        key={`uploaded-file-option-${code}-${mediaOwner}`}
        style={style}
      >
        <SelectOption
          value={{ code, name: code, include, mediaOwner }}
          isSelected={isOptionSelected(code, fileIndex)}
          isMultiple
          hasCheckbox={false}
          isIncludeExcludeAlwaysVisible
          isDeselectAllowed={false}
          onIncludeExcludeChange={(value) => handleIncludeExcludeChange?.(value, fileIndex)}
        >
          <MediaOwnerLabel isReseller={isReseller} mediaOwner={mediaOwner} name={name} />
        </SelectOption>
      </div>
    );
  };
  const renderHeaderComponent = (file: FileList, _fileIndex: number, isHovered: boolean): React.ReactElement => {
    const { available, included, excluded } = getFileStats(file);

    return (
      <>
        {isHovered && <CollapsibleCategoryButtons onRemove={() => onRemoveFile(file.name)} />}
        <CollapsibleListStatsHeader
          available={available}
          included={included}
          excluded={excluded}
          availableLengthSuffix={<SVG src={frameSvg} />}
        />
        <IncludeExcludeToggle
          option={{ ...file, code: file.name }}
          onIncludeExcludeChange={onEntireFileIncludeExclude}
          isAlwaysVisible
          isSelected={file.include !== undefined}
          isOptionNameSelectable={false}
          isHovered={false}
          isDeselectAllowed={false}
        />
      </>
    );
  };

  return (
    <ListOfFiles
      uploadedFiles={uploadedListOfFiles}
      openCategories={openCategories}
      onToggleCategory={onToggleCategory}
      renderItem={renderItem}
      renderHeaderComponent={renderHeaderComponent}
    />
  );
};
export default ListOfFilesWithIncludeExclude;
